import { useContext, useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import { Index, useHits, Configure } from 'react-instantsearch-hooks-web';

import ArrowRight from "/packages/icons/arrow-right.svg";
import ArrowLeft from "/packages/icons/arrow-left.svg";

import dynamic from "next/dynamic";
const Swiper = dynamic(() => import('swiper/react').then(mod => mod.Swiper), { ssr: false });
import { SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

import ProductCard from '@/components/product/ProductCard';
import StoreContext from '@/context/store';
import Button from '@/components/Button';
import { IconRenderer } from '@/components/IconRenderer';
import { AlgoliaSearchProvider } from "@/context/algolia";

import styles from './productSlider.module.scss';
import InlineSVG from '@/components/InlineSVG';
import SkeletonCard from '../../skeletons/SkeletonCard';

export default function ProductSliderBlock({ title, subtitle, icon, category, filters = {}, excludeProductId, mediaType, customIcon }) {
  const { store } = useContext(StoreContext);
  const parsedFilters = buildFilterObject({ category, filters });
  const { root_types, strain, brand, special } = parsedFilters;
  const filterString = buildAlgoliaFilterString(parsedFilters, store);
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <>
    {!hasMounted ? 
      <SkeletonCard />
    :
    <AlgoliaSearchProvider>
      <Index indexName="menu-products-by-rating-production">
        <Configure hitsPerPage={11} filters={filterString} />
        <HitsSwiper
          title={title}
          subtitle={subtitle}
          icon={icon}
          root_types={root_types}
          category={category}
          strain={strain}
          brand={brand}
          special={special}
          slug={category?.slug?.current}
          excludeProductId={excludeProductId}
          mediaType={mediaType}
          customIcon={customIcon}
        />
      </Index>
    </AlgoliaSearchProvider>
}
    </>
  );
}

// 🔧 Helper: Parse filters from Sanity block
function buildFilterObject({ category, filters }) {
  return category?.category
    ? { root_types: category.category }
    : {
      root_types: filters?.root_types,
      brand: filters?.brand,
      strain: filters?.strain,
      special: filters?.special,
    };
}

// 🔧 Helper: Convert filters into Algolia string
function buildAlgoliaFilterString(filters, store) {
  const conditions = [`store_id:${store}`];
  Object.entries(filters).forEach(([key, value]) => {
    if (value) conditions.push(`${key}:"${value}"`);
  });
  return conditions.join(' AND ');
}

function HitsSwiper({ title, subtitle, root_types, strain, brand, special, slug, icon, excludeProductId, category, mediaType, customIcon }) {
  const { hits, error } = useHits();
  const swiperRef = useRef(null);
  const swiperEleId = title?.replace(/[^\w]+/g, '');

  // Determine the icon source
  const resolvedMediaType = category
    ? category.mediaType
    : mediaType;

  const resolvedIcon = category
    ? category.icon
    : mediaType === 'icon'
      ? icon
      : null;

  const resolvedCustomIcon = category
    ? category.customIcon
    : mediaType === 'image'
      ? customIcon
      : null;

  const hasIcon =
    (resolvedMediaType === 'icon' && resolvedIcon) ||
    (resolvedMediaType === 'image' && resolvedCustomIcon?.asset);

  // build the view all link
  const href = buildViewAllLink({ brand, strain, root_types, special, slug });

  const isSkeleton = !error && hits.length === 0;

  const swiperConfig = {
    modules: [Navigation],
    className: styles.productSwiper,
    spaceBetween: 16,
    touchEventsTarget: 'container',
    breakpoints: {
      1024: { slidesPerView: 4, slidesPerGroup: 4 },
      768: { slidesPerView: 3, slidesPerGroup: 3 },
      370: { slidesPerView: 2, slidesPerGroup: 1 },
      0: { slidesPerView: 1.2, slidesPerGroup: 1 },
    },
    navigation: {
      prevEl: `#${swiperEleId}-prev`,
      nextEl: `#${swiperEleId}-next`,
      clickable: true,
    },
    onSwiper: swiper => (swiperRef.current = swiper),
  };

  if (error) return <div>Error loading products: {error.message}</div>;

  return (
    <div className={styles.slider}>
      <div className={styles.header}>
        <div className={styles.header__content}>
          <div className={styles.title}>
            {hasIcon && (
              <div className={styles.icon}>
                {resolvedMediaType === 'icon' && resolvedIcon && (
                  <IconRenderer icon={resolvedIcon} />
                )}

                {resolvedMediaType === 'image' && resolvedCustomIcon?.asset && (
                  <InlineSVG url={urlFor(resolvedCustomIcon).url()} />
                )}
              </div>
            )}
            <div>
              <h2>{title}</h2>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
        </div>
        <Stats href={href} />
      </div>

      <div className={`${styles.container} product-slider`}>
        <Swiper {...swiperConfig}>
          <div className={styles.prev} id={`${swiperEleId}-prev`}>
            <Button type="button" className="slider-arrow" ariaLabel="Previous Slide">
              <ArrowLeft />
            </Button>
          </div>
          <div className={styles.next} id={`${swiperEleId}-next`}>
            <Button type="button" className="slider-arrow" ariaLabel="Next Slide">
              <ArrowRight />
            </Button>
          </div>

          {isSkeleton
            ? Array.from({ length: 4 }).map((_, index) => (
              <SwiperSlide key={`skeleton-${index}`}>
                <SkeletonCard />
              </SwiperSlide>
            ))
            : hits
              .filter(hit => hit.product_id?.toString() !== excludeProductId?.toString())
              .map(hit => (
                <SwiperSlide key={hit.product_id}>
                  <ProductCard hit={hit} />
                </SwiperSlide>
              ))
          }

          <SwiperSlide className={styles.lastSlide}>
            <Link href={href}>
            {hasIcon && (
              <div className={styles.icon}>
                {resolvedMediaType === 'icon' && resolvedIcon && (
                  <IconRenderer icon={resolvedIcon} />
                )}

                {resolvedMediaType === 'image' && resolvedCustomIcon?.asset && (
                  <InlineSVG url={urlFor(resolvedCustomIcon).url()} />
                )}
              </div>
            )}
            View All</Link>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  )
}

function buildViewAllLink({ brand, strain, root_types, special, slug }) {
  if (slug) return `/${slug}`;
  if (brand) return `/brands/${brand.replace(/\s+/g, '-')}`;
  if (strain) return `/shop?strain=${encodeURIComponent(strain)}`;
  if (special) return `/shop?special=${encodeURIComponent(special)}`;
  if (root_types) return `/shop/${root_types}`;
  return `/shop`;
}

function Stats({ href }) {
  const { results } = useHits();

  return (
    <Button type="link" href={href} className="sliderLink" prefetch={false} ariaLabel="View All">
      View All ({results.nbHits})
    </Button>
  );
}